/* eslint-disable jsx-a11y/label-has-associated-control */

'use client';

import React from 'react';

import Image from 'next/image';

import { useRouter } from 'next/navigation';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { z } from 'zod';

import { useToast } from '@/components/ui/use-toast';

import Agent from '@/public/img/newAgents/agente.png';
import Aumenta from '@/public/img/newAgents/aumenta.png';
import Ganancias from '@/public/img/newAgents/ganancias.png';
import Martha from '@/public/img/newAgents/martha.png';

import LogoHondubetSvg from '../svgDesing/LogoHondubetSvg';

const formSchema = z.object({
  nombre: z.string().min(1, { message: 'El nombre es requerido' }).max(15, { message: 'Máximo 15 caracteres' }),
  apellido: z.string().min(1, { message: 'El apellido es requerido' }).max(15, { message: 'Máximo 15 caracteres' }),
  telefono: z.string().min(1, { message: 'El teléfono es requerido' }).max(25, { message: 'Máximo 25 caracteres' }).regex(/^[0-9]+$/, { message: 'Solo se permiten números' }),
  comercio: z.enum(['si', 'no'], {
    errorMap: () => { return { message: 'Selecciona una opción' }; },
  }),
  tipo: z.string().max(20, { message: 'Máximo 20 caracteres' }).optional(),
  ciudad: z.string().min(1, { message: 'La ciudad es requerida' }),
});

const AgentTab = () => {
  const { toast } = useToast();
  const router = useRouter();

  const {
    register,
    formState: { errors },
    reset,
    getValues,
    setValue,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    const data = getValues();
    if (!data.telefono || !data.nombre || !data.apellido || !data.ciudad) {
      toast({
        title: 'Error',
        description: 'Por favor, completa todos los campos requeridos.',
      });
      return;
    }
    if (data.comercio === 'si') {
      if (!data.tipo) {
        toast({
          title: 'Error',
          description: 'El tipo de comercio es requerido',
        });
        return;
      }
    }

    try {
      const response = await fetch('/api/integrations/newAgent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...data, contactado: 'FALSE', tipo: data.tipo || '-' }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      reset();
      toast({
        title: 'Gracias por tu interés en ser agente',
        description: 'En breve nos pondremos en contacto contigo',
      });
      router.push('/');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Hubo un error al enviar el formulario',
      });
      // eslint-disable-next-line no-console
      console.error('Error in onSubmit:', error);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 !gap-0 auto-rows-auto bgAgent min-h-[100vh]">

      {/* ======================= Logo ======================= */}
      <div className="row-span-1 mb-3 md:mb-0">
        <LogoHondubetSvg height="100%" width="100%" className="mx-auto w-48 md:w-96 " />
      </div>
      {/* ======================= Form ======================= */}
      <div className="row-auto md:row-span-2 !row-start-3 md:row-start-2 mt-[20vh] md:mt-0">
        <form
          onSubmit={async (event) => {
            onSubmit(event);
          }}
          className="flex flex-col items-center gap-2 mt-10"
        >

          <div className="flex gap-2">
            {/* Campo Nombre */}
            <div className="flex flex-col gap-2 w-[47vw] md:w-[17vw]">
              <label htmlFor="name">Nombre</label>
              <input
                id="name"
                name="nombre"
                onChange={register('nombre').onChange}
                onBlur={register('nombre').onBlur}
                ref={register('nombre').ref}
                placeholder="Nombre"
                required
                className={`input-form-text h-10 px-2 rounded-md ${errors.nombre ? 'border-red-500' : ''}`}
              />
              {errors.nombre && <span className="text-red-500 text-sm">{errors.nombre.message}</span>}
            </div>

            {/* Campo Apellido */}
            <div className="flex flex-col gap-2 w-[47vw] md:w-[17vw]">
              <label htmlFor="apellido">Apellido</label>
              <input
                id="apellido"
                name="apellido"
                onChange={register('apellido').onChange}
                onBlur={register('apellido').onBlur}
                ref={register('apellido').ref}
                placeholder="Apellido"
                className={`input-form-text h-10 px-2 rounded-md ${errors.apellido ? 'border-red-500' : ''}`}
              />
              {errors.apellido && <span className="text-red-500 text-sm">{errors.apellido.message}</span>}
            </div>
          </div>

          <div className="flex gap-2">
            {/* Campo Teléfono */}
            <div className="flex flex-col gap-2 w-[47vw] md:w-[17vw]">

              <PhoneInput
                searchClass="!w-full"
                buttonClass="input-phone-button"
                dropdownClass="!bg-gray !text-primary_text !h-20 !rounded"
                inputClass="!w-full ![border:none] ![outline:none] !bg-gray !self-stretch !h-10 !shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] !rounded-lg !flex !flex-row !justify-start !py-[15px] ! px-3.5 !box-border !font-text-sm-medium !font-medium !text-sm !text-slategray !min-w-[212px]"
                country="hn"
                onlyCountries={['hn']}
                value={getValues('telefono')}
                onChange={(value) => {
                  setValue('telefono', value, { shouldValidate: true });
                }}
                onBlur={() => { return trigger('telefono'); }}
                inputProps={{
                  placeholder: 'Teléfono',
                  required: true,
                  name: 'telefono',
                  id: 'telefono',
                  className: `w-full mt-2 input-form-text h-10 px-2 rounded-md ${errors.telefono ? 'border-red-500' : ''}`,
                }}
              />
            </div>

            {/* Campo Comercio */}
            <div className="flex flex-col gap-2 w-[47vw] md:w-[17vw]">
              <label htmlFor="comercio">¿Posee local comercial?</label>
              <select
                id="comercio"
                name="comercio"
                ref={register('comercio').ref}
                onChange={register('comercio').onChange}
                onBlur={register('comercio').onBlur}
                required
                className={`w-full p-2 rounded-md input-form-text h-10 px-2 ${errors.comercio ? 'border-red-500' : ''}`}
              >
                <option value="">Seleccione una opción</option>
                <option value="si">Sí</option>
                <option value="no">No</option>
              </select>
              {errors.comercio && <span className="text-red-500 text-sm">{errors.comercio.message}</span>}
            </div>
          </div>

          <div className="flex gap-2">
            {/* Campo Tipo de Comercio */}
            <div className="flex flex-col gap-2 w-[47vw] md:w-[17vw]">
              <label htmlFor="tipo">Tipo de comercio</label>
              <input
                id="tipo"
                name="tipo"
                onChange={register('tipo').onChange}
                onBlur={register('tipo').onBlur}
                ref={register('tipo').ref}
                placeholder="Tipo de comercio"
                className={`input-form-text h-10 px-2 rounded-md ${errors.tipo ? 'border-red-500' : ''}`}
              />
              {errors.tipo && <span className="text-red-500 text-sm">{errors.tipo.message}</span>}
            </div>

            {/* Campo Ciudad */}
            <div className="flex flex-col gap-2 w-[47vw] md:w-[17vw]">
              <label htmlFor="ciudad">Ciudad</label>
              <input
                id="ciudad"
                name="ciudad"
                ref={register('ciudad').ref}
                onChange={register('ciudad').onChange}
                onBlur={register('ciudad').onBlur}
                placeholder="Ciudad"
                required
                className={`input-form-text h-10 px-2 rounded-md ${errors.ciudad ? 'border-red-500' : ''}`}
              />
              {errors.ciudad && <span className="text-red-500 text-sm">{errors.ciudad.message}</span>}
            </div>
          </div>

          <button
            type="submit"
            className="mx-auto p-2 rounded-md text-sivarbet-secondary-bg mt-5 font-bold bg-sivarbet-secondary hover:scale-105 transition-all duration-300 shadow-[0_4px_13px_rgba(110,184,47,0.7)]"
          >
            Quiero ser agente
          </button>
        </form>
      </div>

      {/* ======================= Images ======================= */}
      <div className="row-span-2 row-start-2 md:row-span-2 md:row-start-1 md:col-start-2  md:relative flex md:h-full mt-10 md:mt-0">
        <Image src={Martha} alt="Martha" layout="responsive" width={10} height={10} className="mask absolute !right-0 top-10 md:!top-4 !w-[50vw] md:!w-[85vh] h-auto z-0" />
        <div className="flex flex-col gap-1 !z-20 absolute !left-[5vw] md:!left-0 animationPulse">
          <Image src={Aumenta} alt="Aumenta" layout="responsive" width={100} height={100} className="!w-[40vw] mt-[-10vh] md:mt-2 md:top-0 md:!w-[30vw] h-auto  md:ms-[-5vw] !z-10 relative " />
          <Image src={Ganancias} alt="Ganancias" layout="responsive" width={100} height={100} className="!w-[40vw] mt-[-3vh] md:!w-[30vw] ms-[3vw] md:ms-[-3vw] md:mt-[-6vh] h-auto !z-10 relative" />
        </div>

        <Image src={Agent} alt="Agente" layout="responsive" width={100} height={100} className="!w-[32vw] md:!w-[20vw] h-auto z-0 absolute md:top-[63vh] top-[25vh] md:left-[0vw] left-[45vw]" />
      </div>
    </div>
  );
};

export default AgentTab;
